import React, { useEffect, useRef, useState } from 'react';
import PropTypes from "prop-types";
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ActionCreator from "../../../store/actions/index";
import { compose } from 'recompose';
import SingleCar from './SingleCar';
import { Collapse, Typography, withStyles } from "@material-ui/core";
import RouteList from "./RouteList";
import SingleCarButtons from "./SingleCarButtons";
import { Scrollbars } from "react-custom-scrollbars";
import moment from 'moment-timezone';
import { useQuery } from '../../../utils/helpers';
import { displayDateTimeFormat } from '../../../utils/staticData';
import withFilters from '../../HOC/withFilters';
import { withRouter } from 'react-router-dom';
import t from "../../../languages/pl";
import usePrevious from '../../../utils/hooks/usePrevious';

export const carListWidth = 340;


function CarList(props) {
  const { classes, selectedCar, cars, routes, currentRoute, onCarsChange } = props;
  const query = useQuery();
  const urlId = query.get("id");
  const date = query.get("date");
  const [open, setOpen] = useState("");
  const [visibleCars, setVisibleCars] = useState([]);
  const [carsCount, setCount] = useState(11);
  const [scrollEnd, setScrollEnd] = useState(false);


  const prevCurrentRoute = usePrevious(() => {
    if (currentRoute)
      return currentRoute.w_trasie;
  });

  const scrollbar = useRef(null);

  useEffect(() => {
    if (!urlId)
      props.getCarsForMap();
    else {
      if (date) {
        props.changeStartDate(moment.unix(date));
        props.changeEndDate(moment.unix(date));
      } else {
        props.changeStartDate(moment().subtract(3, 'days').startOf("day"));
        props.changeEndDate(moment().endOf('day'));
      }
      props.getCarsForMap(urlId);
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!urlId)
        props.getCarsForMap();
      else
        props.getCarsForMap(urlId);
    }, 60_000);
    return () => clearInterval(intervalId);
  }, [urlId]);

  useEffect(() => {
    if (!cars) {
      return;
    }

    if (cars.length < carsCount) {
      setVisibleCars(cars);
      setScrollEnd(true);
    } else {
      setVisibleCars(cars.slice(0, carsCount - 1));
      setScrollEnd(false);
    }

    onCarsChange(cars);
  }, [cars, carsCount]);

  useEffect(() => {
    if (selectedCar && !visibleCars.find(car => car.auto_id === selectedCar.auto_id)) {
      scrollbar.current.scrollToBottom();
    } else if (selectedCar) {
      scrollElementToTop();
    }
  }, [selectedCar, visibleCars, routes]);

  const handleBottomReached = e => {
    if (e.top >= 0.99 && !scrollEnd)
      setCount(count => count + 5);
  };

  const scrollElementToTop = () => {
    if (selectedCar !== null) {
      let element = document.getElementById(selectedCar.auto_id);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    }
  };

  const handleListExpand = (id) => {
    props.clearRoutes();
    if (id !== open) {
      setOpen(id);
      let car = cars.find(car => car.auto_id === id);
      props.setSelectedCar(car);
    } else {
      setOpen("");
      props.clearSelectedCar();
    }
  };

  useEffect(() => {
    selectedCar ? setOpen(selectedCar.auto_id) : setOpen("");
  }, [selectedCar, cars]);

  useEffect(() => {
    if (selectedCar && currentRoute) {
      let interval = 300000;
      let fetchCurrentRoute;
      if (currentRoute.w_trasie) {
        interval = 30000;
      } else {
        interval = 300000;
      }
      fetchCurrentRoute = setTimeout(() => {
        props.fetchCurrentRoute(selectedCar.auto_id);
      }, interval);
      return () => {
        if (prevCurrentRoute !== currentRoute.w_trasie) {
          clearTimeout(fetchCurrentRoute);
        }
      };
    }
  }, [selectedCar, currentRoute]);

  useEffect(() => {
    if (urlId && cars.length === 1)
      props.setSelectedCar(cars.find(car => car.rejestracja === urlId));
  }, [urlId, cars]);

  const handleListMount = id => {
    props.getRoutes(id);
  };

  const handleAddressReveal = id => {
    props.addressReveal(id);
  };

  const handleRedirectToCarDetails = registrationNo => {
    props.history.push({
      pathname: "/cars",
      search: `?ids=${registrationNo}`
    });
  };

  const listCars = (open, callback, addressRevealCallback, listMountedCallback) => {
    if (!visibleCars || visibleCars.length === 0)
      return (<Typography
        variant="subtitle1"
        className={classes.noCars}>
        {t.noDataForSpecifiedCriteria}
      </Typography>);
    return visibleCars.map((data) => {
      return (
        <div key={data.auto_id} className="carListElement">
          <SingleCar
            id={data.auto_id}
            car={data}
            type={data.typ}
            status={data.stan_gps}
            registrationNo={data.rejestracja}
            driver={data.kierowca}
            name={data.marka + " " + data.model}
            distance={data.dystans}
            speed={data.predkosc}
            fuelType={data.paliwo}
            fuelTypeId={data.paliwo_id}
            fuelLevel={data.stan_paliwa}
            fuelReserve={data.rezerwa}
            fuelPercent={data.stan_paliwa_procent}
            address={data.adres_gps}
            lastGpsUpdate={moment.unix(data.stan_gps_data).format(displayDateTimeFormat)}
            selected={open === data.auto_id}
            onClick={callback.bind(this, data.auto_id)}
            onAddressReveal={addressRevealCallback.bind(this, data.auto_id)}
            onRegistrationClick={handleRedirectToCarDetails}
            currentPosition={data.pozycja_gps}
            archived={data.zarchiwizowane}
          />
          <Collapse
            in={open === data.auto_id}
            timeout="auto"
            unmountOnExit
          >
            <SingleCarButtons
              registrationNo={data.rejestracja}
            />
            <RouteList
              routes={routes}
              onMounted={listMountedCallback.bind(this, data.auto_id)}
              onGetLastRoutes={() => props.getRoutes(data.auto_id, true)}
            />
          </Collapse>
        </div>
      );
    });
  };

  return (
    <div className={classes.container}>
      <Scrollbars
        ref={scrollbar}
        onScrollFrame={handleBottomReached}
      >
        <div className={classes.carsList}>
          {listCars(open, handleListExpand, handleAddressReveal, handleListMount)}
        </div>
      </Scrollbars>
    </div>
  );
}

const useStyles = theme => ({
  carsList: {
    overflow: "hidden",
    minWidth: carListWidth,
  },
  container: {
    height: 'calc(100vh - 146px)'
  },
  noCars: {
    textAlign: "center",
    margin: theme.spacing(2)
  }
});

const mapStateToProps = state => {
  return {
    selectedCar: state.mapCarsReducer.selectedCar,
    cars: state.mapCarsReducer.cars,
    routes: state.mapRoutesReducer.routes,
    currentRoute: state.mapRoutesReducer.currentRoute,
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(ActionCreator, dispatch);
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withStyles(useStyles),
  withRouter
)(withFilters(CarList, true, true));

CarList.propTypes = {
  classes: PropTypes.object,
  onCarsChange: PropTypes.func,
  //routes
  match: PropTypes.object,
  //redux
  selectedCar: PropTypes.object,
  cars: PropTypes.array,
  routes: PropTypes.array,
  currentRoute: PropTypes.object,
  getCarsForMap: PropTypes.func,
  setSelectedCar: PropTypes.func,
  clearSelectedCar: PropTypes.func,
  getRoutes: PropTypes.func,
  clearRoutes: PropTypes.func,
  addressReveal: PropTypes.func,
  changeStartDate: PropTypes.func,
  changeEndDate: PropTypes.func,
  getCurrentRoute: PropTypes.func,
  fetchCurrentRoute: PropTypes.func,
  //router
  history: PropTypes.object
};
